import { useMemo } from 'react'
import useQueryParam from './useQueryParam'
import {
    TYPE_PARAM,
    PAGE_PARAM,
    PAGE_SIZE_PARAM,
    SORT_PARAM,
    LANGUAGE_PARAM,
    ASSIGNMENT_SEARCH_PARAM,
    OWNER_PARAM,
    SEARCH_TYPE_PARAM,
    PERMISSION_LEVEL_PARAM
} from '../constants/query-params'
import { AssignmentSearchTypes } from '../constants/assignment-search-types'
import { DEFAULT_ASSIGNMENT_SORTING_TYPE } from '../constants/sorting-types'

// Stable empty object to prevent unnecessary re-renders
const EMPTY_OVERRIDES = {}

/**
 * Hook to get assignment filters from URL parameters
 * 
 * This hook extracts query parameters from the URL and formats them into
 * a queryArgs object that can be used for fetching assignments via RTK Query.
 * 
 * @param {Object} overrides - Optional parameter overrides
 * @returns {Object} Formatted query arguments for the assignments API
 */
const useAssignmentFilters = (overrides = EMPTY_OVERRIDES) => {
    const assignmentSearch = useQueryParam(ASSIGNMENT_SEARCH_PARAM)
    const sort = useQueryParam(SORT_PARAM) || DEFAULT_ASSIGNMENT_SORTING_TYPE
    const typeFilter = useQueryParam(TYPE_PARAM)
    const languageFilter = useQueryParam(LANGUAGE_PARAM)
    const page = useQueryParam(PAGE_PARAM)
    const pageSize = useQueryParam(PAGE_SIZE_PARAM)
    const ownerFilter = useQueryParam(OWNER_PARAM)
    const permissionLevelFilter = useQueryParam(PERMISSION_LEVEL_PARAM)
    const searchTypeParam = useQueryParam(SEARCH_TYPE_PARAM)
    const searchType = searchTypeParam 
        ? parseInt(searchTypeParam) 
        : AssignmentSearchTypes.MINE

    return useMemo(() => ({
        name: assignmentSearch,
        sort,
        type: typeFilter ? typeFilter.split(',') : null,
        language: languageFilter ? languageFilter.split(',') : null,
        page,
        pageSize,
        ownerIds: ownerFilter ? ownerFilter.split(',') : null,
        permissionLevel: permissionLevelFilter,
        searchType,
        ...overrides
    }), [
        assignmentSearch, 
        sort, 
        typeFilter, 
        languageFilter,
        page, 
        pageSize, 
        ownerFilter,
        permissionLevelFilter,
        searchType,
        overrides
    ])
}

export default useAssignmentFilters 